import { PushNotifications, type Token, type PushNotificationSchema, type ActionPerformed } from "@capacitor/push-notifications";
import { supabase } from "@/integrations/supabase/client";
import { isNative, getPlatform } from "./platform";

type NavigateFn = (path: string) => void;

/**
 * Registers the device for APNs (iOS) or FCM (Android) push, stores the
 * token in `public.device_tokens` scoped to the current user, and wires
 * tap-to-navigate deep links into React Router.
 *
 * Safe to call on web — no-ops when not running inside Capacitor.
 */
export async function initNativePush(navigate: NavigateFn): Promise<void> {
  if (!isNative()) return;

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return;

  PushNotifications.addListener("registration", async (token: Token) => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;

    await supabase.from("device_tokens").upsert(
      {
        user_id: userId,
        platform: getPlatform(),
        token: token.value,
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: "user_id,token" }
    );
  });

  PushNotifications.addListener("registrationError", (err) => {
    console.warn("[push] registration error", err);
  });

  PushNotifications.addListener(
    "pushNotificationReceived",
    (_notification: PushNotificationSchema) => {
      // Foreground handling is left to the app-level toast/inbox UI.
    }
  );

  PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action: ActionPerformed) => {
      const path = action.notification.data?.path;
      if (typeof path === "string" && path.startsWith("/")) {
        navigate(path);
      }
    }
  );

  await PushNotifications.register();
}
